import React from 'react';
import { SimulationConfig, BallStyle, EscapeColorMode, BallTexture } from '@/types';
import { ControlGroup } from './ControlGroup';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { CollapsibleSection } from './CollapsibleSection';
import { VISUAL_PRESETS } from '@/data/presets';
import { motion } from 'motion/react';
import { 
  Palette, 
  Square, 
  Circle, 
  Target, 
  Box, 
  Zap, 
  Wand2 
} from 'lucide-react';

interface VisualStylesSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  getLockInfo?: (id: string) => { isLocked: boolean; min_plan?: string };
}

export const VisualStylesSection: React.FC<VisualStylesSectionProps> = ({ config, setConfig, isLocked, getLockInfo }) => {
  const isLockedFn = (id: string) => getLockInfo?.(id).isLocked || false;
  const planFn = (id: string) => getLockInfo?.(id).min_plan as any;

  const ringPatterns: EscapeColorMode[] = ['RAINBOW', 'ALTERNATING', 'GRADIENT', 'UNIFORM', 'INDIVIDUAL'];
  const isEscape = config.mode === 'ESCAPE' || config.mode === 'RHYTHMIC_ESCAPE' || config.enableEscape;
  const escapeMsg = "This setting is designed for ESCAPE mode.";

  const handleIndividualColorChange = (index: number, color: string) => {
    setConfig(p => {
        const current = Array.isArray(p.escapeIndividualColors) ? p.escapeIndividualColors : [];
        const newColors = [...current];
        newColors[index] = color;
        return { ...p, escapeIndividualColors: newColors };
    });
  };

  const ColorInput = ({ label, value, onChange, tooltip, settingKey, disabled, disabledMessage, modes }: { label: string, value: string, onChange: (v: string) => void, tooltip?: string, settingKey?: string, disabled?: boolean, disabledMessage?: string, modes?: string[] }) => (
    <ControlGroup label={label} tooltip={tooltip} settingKey={settingKey} disabled={disabled} disabledMessage={disabledMessage} modes={modes} darkMode={config.darkMode}>
        <div className={`flex items-center gap-3 p-1.5 rounded-2xl border ${config.darkMode ? 'bg-cyan-500/5 border-cyan-500/10' : 'bg-white border-neutral-200'} shadow-inner`}>
            <div className="relative group">
                <div className="w-10 h-10 rounded-full border-2 border-white/10 shadow-lg cursor-pointer transition-transform group-hover:scale-105 active:scale-95" style={{ backgroundColor: value }} />
                <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
            </div>
            <input 
                type="text" 
                value={value} 
                onChange={e => onChange(e.target.value)} 
                className={`flex-1 px-3 py-2 text-[10px] font-mono font-medium border rounded-xl outline-none transition-all ${
                    config.darkMode 
                    ? 'bg-cyan-500/5 border-cyan-500/10 text-white focus:border-cyan-400/30' 
                    : 'bg-neutral-50 border-neutral-100 text-neutral-600 focus:border-cyan-600/30'
                }`} 
            />
        </div>
    </ControlGroup>
  );

  return (
    <div className="flex flex-col gap-2">
      <CollapsibleSection 
        title="Visual Presets" 
        icon={Wand2} 
        darkMode={config.darkMode} 
        defaultOpen
        settingKey="visualPresets"
        isLocked={isLockedFn('visualPresets')}
        lockRequiredPlan={planFn('visualPresets')}
        onUpgradeClick={() => {}}
      >
        <div className="flex flex-col gap-6 mb-4 mt-4">
            <div className="flex flex-col gap-3">
                <span className="text-[9px] font-medium uppercase tracking-widest opacity-20 px-1">Sphere Templates</span>
                <div className="grid grid-cols-2 gap-2">
                    {VISUAL_PRESETS.filter(p => !p.name.toLowerCase().includes('environment') && !p.name.toLowerCase().includes('world')).map(p => (
                        <button
                            key={p.name}
                            onClick={() => setConfig(prev => ({ ...prev, ...p.settings }))}
                            className={`px-4 py-3 rounded-2xl text-[9px] font-medium uppercase tracking-wider transition-all h-12 flex items-center justify-center text-center ${
                                config.darkMode 
                                ? 'bg-white/5 hover:bg-white/10 text-white/80 border border-white/5' 
                                : 'bg-white hover:bg-neutral-50 text-neutral-600 border border-neutral-200'
                            }`}
                        >
                            {p.name}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <span className="text-[9px] font-medium uppercase tracking-widest opacity-20 px-1">Global Presets</span>
                <div className="grid grid-cols-2 gap-2">
                    {VISUAL_PRESETS.filter(p => p.name.toLowerCase().includes('environment') || p.name.toLowerCase().includes('world')).map(p => (
                        <button
                            key={p.name}
                            onClick={() => setConfig(prev => ({ ...prev, ...p.settings }))}
                            className={`px-4 py-3 rounded-2xl text-[9px] font-medium uppercase tracking-wider transition-all h-12 flex items-center justify-center text-center ${
                                config.darkMode 
                                ? 'bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20' 
                                : 'bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 shadow-sm'
                            }`}
                        >
                            {p.name}
                        </button>
                    ))}
                </div>
            </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="World & Boundary" 
        icon={Square} 
        darkMode={config.darkMode}
        settingKey="worldAndBoundary"
        isLocked={isLockedFn('worldAndBoundary')}
        lockRequiredPlan={planFn('worldAndBoundary')}
      >
        <div className="flex flex-col gap-4 mb-4 mt-4">
            <SmartInput 
                label="Border Thickness" 
                min={1} max={50} 
                value={config.containerLineWidth || 4} 
                onChange={v => setConfig(p => ({...p, containerLineWidth: v}))} 
                darkMode={config.darkMode} 
                settingKey="containerLineWidth"
            />
            <ColorInput 
                label="Border Chrome" 
                value={config.borderColor || '#1a1a1a'} 
                onChange={v => setConfig(p => ({...p, borderColor: v}))} 
                settingKey="borderColor"
            />
            <ColorInput 
                label="Ambient Canvas" 
                value={config.outerColor} 
                onChange={v => setConfig(p => ({...p, outerColor: v}))} 
                settingKey="outerColor"
            />
        </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="RGB Synthesis Lab" 
        icon={Zap} 
        darkMode={config.darkMode}
        settingKey="rgbEngine"
        isLocked={isLockedFn('rgbEngine')}
        lockRequiredPlan={planFn('rgbEngine')}
      >
        <div className="flex flex-col gap-6 mb-4 mt-4">
            <div className="p-4 rounded-3xl border border-white/5 bg-white/5">
                <ControlGroup label="Master Synthesis Engine" settingKey="rgbLighting" darkMode={config.darkMode}>
                    <Toggle active={config.rgbLighting} onClick={() => setConfig(p => ({...p, rgbLighting: !p.rgbLighting}))} color="bg-cyan-500" />
                </ControlGroup>
            </div>
            
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 px-1 mb-1">
                     <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                     <span className="text-[10px] font-black uppercase tracking-widest opacity-40">Module Pulse Sync</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    {[
                        { label: 'Ball Pulse', key: 'useBallRGB' },
                        { label: 'VFX Glow', key: 'useFXRGB' },
                        { label: 'Trail Flux', key: 'useTrailRGB' },
                        { label: 'Edge Aura', key: 'useContainerRGB' }
                    ].map(({ label, key }) => (
                        <div key={key} className={`p-4 rounded-2xl border transition-all ${config.darkMode ? 'bg-white/5 border-white/5 text-white' : 'bg-zinc-50 border-zinc-100'}`}>
                            <ControlGroup label={label} settingKey={key} darkMode={config.darkMode}>
                                <Toggle active={(config as any)[key]} onClick={() => setConfig(p => ({...p, [key]: !(p as any)[key]}))} />
                            </ControlGroup>
                        </div>
                    ))}
                </div>
            </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection 
        title="Blast & FX Colors" 
        icon={Box} 
        darkMode={config.darkMode}
        settingKey="blastFXColor"
        isLocked={isLockedFn('blastFXColor')}
        lockRequiredPlan={planFn('blastFXColor')}
      >
        <div className="flex flex-col gap-4 mb-4 mt-4">
            <ColorInput label="Internal Core" value={config.innerColor} onChange={v => setConfig(p => ({...p, innerColor: v}))} settingKey="innerColor" />
            <ColorInput label="Pulse Rings" value={config.collisionWaveColor} onChange={v => setConfig(p => ({...p, collisionWaveColor: v}))} settingKey="collisionWaveColor" />
            <div className="flex flex-col gap-4">
                <ColorInput label="Blast Flare" value={config.blastColor} onChange={v => setConfig(p => ({...p, blastColor: v}))} settingKey="blastColor" />
                <ControlGroup label="Sync with Rings" settingKey="blastMatchRingColor" darkMode={config.darkMode}>
                    <Toggle active={config.blastMatchRingColor} onClick={() => setConfig(p => ({...p, blastMatchRingColor: !p.blastMatchRingColor}))} />
                </ControlGroup>
            </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};
